import {
  ChangeEventHandler,
  FocusEventHandler,
  FormEventHandler,
  forwardRef,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Field from './Field';
import { baseLabelStyles, baseTextFieldStyles } from './sharedFieldStyles';

export const DEFAULT_MIN_DATE = '1900-01-01';
export const DEFAULT_MAX_DATE = '9999-12-31';

export type InputPrimitiveProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'placeholder' | 'id'
> & {
  /**
   * The id is required to properly associate the input with its label and error messages for accessibility reasons.
   */
  id: string;
  /**
   * The type of the input element. The submit, file, and button types are not allowed as those have specific components to be used.
   */
  type?: Exclude<HTMLInputTypeAttribute, 'submit' | 'file' | 'button'>;
  /**
   * Custom validation messages for the different validity states.
   */
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
};

/**
 * The InputPrimitive component is a low-level component that renders an input element
 * with additional functionality for control when the component is invalid based on user interaction and if empty.
 * The `:user-valid` pseudo class couldn't be used as we support older versions of Safari.
 * It is used by the higher-level Input component, but can also be used directly if you need more control over the input element.
 *
 * Note: Considering BaseUI Input as it includes more complete implementation for the different states and data attributes
 */
export const InputPrimitive = forwardRef<HTMLInputElement, InputPrimitiveProps>(
  (
    {
      type = 'text',
      onChange,
      onBlur,
      onInvalid,
      validationMessages = {},
      ...props
    },
    ref,
  ) => {
    const [isEmpty, setIsEmpty] = useState(
      !(props.value || props.defaultValue),
    );
    const [isValid, setIsValid] = useState(true);

    const checkInputValidity = (input: HTMLInputElement) => {
      const valid = !!input.validity.valid;
      if (!valid) {
        const constraint = Object.keys(validationMessages).find(
          (key) => input.validity[key as keyof ValidityState],
        ) as keyof typeof validationMessages | undefined;

        const message =
          constraint && validationMessages[constraint]
            ? validationMessages[constraint]
            : input.validationMessage;

        /**
         * * sets the validation message that is also displayed in native popup message
         * ! this will need to be cleared before checking the validity again
         * ! because it will set the validity of `customError` to true
         */
        input.setCustomValidity(message);
      }
      return valid;
    };
    // Set valid data attribute on input invalid event to style the input trigger by submitting the form
    const handleInvalid: FormEventHandler<HTMLInputElement> = (event) => {
      const input = event.currentTarget;

      setIsValid(checkInputValidity(input));

      onInvalid?.(event);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
      const input = event.currentTarget;

      if (!isEmpty && !isValid) {
        setIsValid(checkInputValidity(input));
      }

      onBlur?.(event);
    };

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      const input = event.currentTarget;
      /*
       * not really a good, consistent way to check if the input is empty (has no value),
       * especially since not allowing the placeholder
       */
      setIsEmpty(!input.value.trim());

      // !Clear the custom validity message `customError` before checking the validity again
      input.setCustomValidity('');

      // Update the validity state on change if the input is in invalid state
      if (!isValid) {
        setIsValid(checkInputValidity(input));
      }

      onChange?.(event);
    };

    let { min, max } = props;
    if (type === 'date') {
      min ??= DEFAULT_MIN_DATE;
      max ??= DEFAULT_MAX_DATE;
    }

    return (
      <input
        type={type}
        data-component="corso-input"
        data-empty={isEmpty ? '' : undefined}
        data-invalid={isValid ? undefined : ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onInvalid={handleInvalid}
        ref={ref}
        min={min}
        max={max}
        {...props}
      />
    );
  },
);

type ValidationConstraint = Exclude<keyof ValidityState, 'valid'>;

/**
 * The Input component is a higher-level component that builds on top of the InputPrimitive to provide a complete input field with a label, description, and error message display.
 * It handles the association between the input and its label and error messages for accessibility,
 * and provides styling for the different states of the input (default, focused, hovered, disabled, invalid).
 */
export default function Input({
  id,
  label,
  className,
  description,
  hideLabel = false,
  ...inputProps
}: {
  /**
   * The label is required for accessibility reasons, but can be visually hidden using the `hideLabel` prop if the design requires it.
   * If `hideLabel` is true, the label will still be available to assistive technologies.
   */
  label: string;
  /**
   * Whether to visually hide the label. The label will still be available to assistive technologies if hidden. This is useful when the design requires a more compact input without a visible label, but you still want to provide context for screen readers.
   */
  hideLabel?: boolean;
  /**
   * A description for the input, providing additional context or instructions. This is typically displayed below the input field.
   */

  description?: ReactNode;
  /**
   * Custom validation messages for different validation constraints. This allows you to provide specific error messages for different types of validation errors.
   */
  validationMessages?: Partial<Record<ValidationConstraint, string>>;
} & InputPrimitiveProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // This watches for changes in the input's validity and sets the error message accordingly
  useEffect(() => {
    const target = ref.current;

    const observer = new MutationObserver(() => {
      if (!target) return;

      if (target.validity.valid) {
        setErrorMessage(null);
      } else {
        setErrorMessage(target.validationMessage);
      }
    });

    if (target) {
      observer.observe(target, {
        attributes: true,
        attributeFilter: ['data-invalid'],
      });
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Field.Root className={twMerge(className, 'relative')}>
      <InputPrimitive
        // oxlint-disable-next-line react/jsx-props-no-spreading -- the props extend the input primitive props
        {...inputProps}
        id={id}
        className={twMerge(
          'peer',
          baseTextFieldStyles,
          hideLabel && 'py-3',
          // datepicker specific styles to hide the default pattern text
          'data-empty:text-transparent data-empty:focus:text-on-surface',
          // number specific styles to remove the spinner buttons
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        )}
        ref={ref}
        aria-describedby={`${id}-help-text ${id}-error-text`}
      />
      <Field.Label
        className={twMerge(hideLabel ? 'sr-only' : baseLabelStyles)}
        htmlFor={id}
      >
        {`${label}${inputProps.required ? '' : ' (optional)'}`}
      </Field.Label>
      {errorMessage && (
        <Field.ErrorText id={`${id}-error-text`}>
          {errorMessage}
        </Field.ErrorText>
      )}
      {description && !errorMessage && (
        <Field.HelpText
          id={`${id}-help-text`}
          className="text-on-surface-variant peer-disabled:text-on-surface/(--state-disabled-mix) peer-data-empty:peer-disabled:text-on-surface/(--state-disabled-mix)"
        >
          {description}
        </Field.HelpText>
      )}
    </Field.Root>
  );
}
